import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { EyeOff, Plus, X, ChevronDown, ChevronUp } from "lucide-react";
import { useApi } from "../hooks/useApi";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { fmtDate } from "../lib/dates";

const err = (e) => toast.error(formatError(e.response?.data?.detail) || "Something went wrong");

function AddMember({ excluded, onAdded }) {
  const [q, setQ] = useState("");
  const [note, setNote] = useState("");
  const { data: roster } = useApi("/members/roster");
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s || !roster) return [];
    return roster.filter((m) => !excluded.has(m.id) && (m.name.toLowerCase().includes(s) || (m.member_id || "").toLowerCase().includes(s))).slice(0, 6);
  }, [q, roster, excluded]);
  const add = async (m) => {
    try { await api.post("/admin/birthdays/opt-out", { member_id: m.id, note }); toast.success(`${m.name} will not be celebrated`); setQ(""); setNote(""); onAdded(); } catch (e) { err(e); }
  };
  return (
    <div className="rounded-xl border border-gold/20 bg-parchment-card p-3">
      <div className="flex flex-wrap gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search member to add…" className="w-60" data-testid="bday-optout-search" />
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason / note (optional)" className="flex-1 min-w-[200px]" data-testid="bday-optout-note" />
      </div>
      {matches.length > 0 && (
        <ul className="mt-2 divide-y divide-gold/15 rounded-lg bg-white/70" data-testid="bday-optout-matches">
          {matches.map((m) => (
            <li key={m.id} className="flex items-center justify-between px-3 py-1.5 text-sm">
              <span>{m.name}{m.member_id ? <span className="ml-1 text-xs text-muted-foreground">{m.member_id}</span> : null}</span>
              <Button size="sm" variant="ghost" onClick={() => add(m)} className="text-crimson" data-testid={`bday-optout-add-${m.id}`}><Plus size={14} /> Add</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function BirthdayOptOut({ onChanged }) {
  const [open, setOpen] = useState(false);
  const { data, reload } = useApi("/admin/birthdays/opt-out");
  const list = data || [];
  const excluded = useMemo(() => new Set((data || []).map((m) => m.id)), [data]);
  const changed = () => { reload(); onChanged?.(); };
  const remove = async (m) => {
    if (!window.confirm(`Allow ${m.name}'s birthday to be celebrated again?`)) return;
    try { await api.delete(`/admin/birthdays/opt-out/${m.id}`); toast.success("Removed from the list"); changed(); } catch (e) { err(e); }
  };
  return (
    <div className="mt-4 rounded-2xl border border-gold/30" data-testid="bday-optout">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-4 py-3 text-left" data-testid="bday-optout-toggle">
        <span className="flex items-center gap-2 text-sm font-semibold text-crimson"><EyeOff size={15} className="text-gold" /> Members who prefer no birthday celebration <span className="rounded-full bg-stone-200 px-2 text-[11px] text-stone-700" data-testid="bday-optout-count">{list.length}</span></span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="space-y-3 border-t border-gold/15 p-3">
          <p className="text-xs text-muted-foreground">Members on this list are left out of the monthly birthday list, WhatsApp posts, the 8am automatic greeting, the portal birthday strip and member-to-member wishes.</p>
          <AddMember excluded={excluded} onAdded={changed} />
          {list.length === 0 && <p className="text-sm text-muted-foreground" data-testid="bday-optout-empty">Nobody has opted out.</p>}
          <ul className="divide-y divide-gold/15 rounded-xl bg-white/60">
            {list.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`bday-optout-row-${m.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-crimson">{m.name}{m.member_id ? <span className="ml-1 text-xs font-normal text-muted-foreground">{m.member_id}</span> : null}</p>
                  <p className="text-xs text-muted-foreground">{m.note || "No note"} · added by {m.added_by || "—"}{m.added_at ? ` on ${fmtDate(m.added_at)}` : ""}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => remove(m)} className="text-crimson" data-testid={`bday-optout-remove-${m.id}`}><X size={14} /> Remove</Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
